import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  InputAdornment,
  alpha,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Newspaper,
  Link2,
  Sparkles,
  ExternalLink,
  Image as ImageIcon,
  CheckCircle2,
  Globe,
  Upload,
  Trash2,
} from 'lucide-react';
import axiosInstance, { API_URL } from '../utils/axios';
import { getNewsImageUrl } from '../utils/helpers';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';

const generateSlug = (text) => {
  return (text || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const INITIAL_FORM = {
  title: '',
  slug: '',
  summary: '',
  content: '',
  type: 'press_release',
  media_source: '',
  external_url: '',
  cover_image: '',
  image_fit: 'contain',
  image_alt: '',
  author: 'Reseller Bazzar Desk',
  status: 'published',
  is_featured: false,
  tags: '',
  published_date: new Date().toISOString().split('T')[0],
};

const NewsPrModal = ({ open, onClose, onSuccess, editData }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [fetchingOg, setFetchingOg] = useState(false);
  const [ogSuccess, setOgSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);

  // Cover image management state
  const [imageMode, setImageMode] = useState('upload'); // 'upload' | 'url'
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingExternalImage, setSavingExternalImage] = useState(false);
  const [proxyImageSrc, setProxyImageSrc] = useState('');
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || '',
        slug: editData.slug || '',
        summary: editData.summary || '',
        content: editData.content || '',
        type: editData.type || 'press_release',
        media_source: editData.media_source || '',
        external_url: editData.external_url || '',
        cover_image: editData.cover_image || '',
        image_fit: editData.image_fit || 'contain',
        image_alt: editData.image_alt || '',
        author: editData.author || 'Reseller Bazzar Desk',
        status: editData.status || 'published',
        is_featured: !!editData.is_featured,
        tags: Array.isArray(editData.tags) ? editData.tags.join(', ') : '',
        published_date: editData.published_date
          ? new Date(editData.published_date).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      });
      setAutoSlug(false);
      setOgSuccess(false);
      if (editData.cover_image && editData.cover_image.startsWith('http')) {
        setImageMode('url');
      } else {
        setImageMode('upload');
      }
    } else {
      setFormData(INITIAL_FORM);
      setAutoSlug(true);
      setOgSuccess(false);
      setImageMode('upload');
    }
    setErrorMsg('');
    setProxyImageSrc('');
    setImageLoadFailed(false);
    setUploadingImage(false);
    setSavingExternalImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [editData, open]);

  // Handle direct file upload from computer/device
  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('news_image', file);
      const res = await axiosInstance.post('/admin/news/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        skipNotify: true,
      });

      if (res.data?.success && res.data.filename) {
        handleChange('cover_image', res.data.filename);
        setProxyImageSrc('');
        setImageLoadFailed(false);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setErrorMsg(err.response?.data?.msg || 'Failed to upload image file.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Download and permanently save external OG image to Reseller Bazzar storage
  const handleSaveExternalImage = async () => {
    if (!formData.cover_image || !formData.cover_image.startsWith('http')) return;
    setSavingExternalImage(true);
    setErrorMsg('');
    try {
      const res = await axiosInstance.post('/admin/news/save-external-image', { url: formData.cover_image });
      if (res.data?.success && res.data.filename) {
        handleChange('cover_image', res.data.filename);
        setProxyImageSrc('');
        setImageLoadFailed(false);
      }
    } catch (err) {
      console.warn('Failed to save external image:', err);
      setErrorMsg('Could not save external image to server. The original link will be used instead.');
    } finally {
      setSavingExternalImage(false);
    }
  };

  // Fetch Open Graph Metadata from external URL
  const handleFetchOg = async (urlToFetch) => {
    const targetUrl = (urlToFetch || formData.external_url || '').trim();
    if (!targetUrl) {
      setErrorMsg('Please enter a PR website link to fetch details.');
      return;
    }

    setFetchingOg(true);
    setErrorMsg('');
    setOgSuccess(false);

    try {
      const res = await axiosInstance.post('/admin/news/fetch-og', { url: targetUrl });
      if (res.data?.success && res.data?.data) {
        const og = res.data.data;
        setFormData((prev) => ({
          ...prev,
          title: og.title || prev.title,
          slug: autoSlug && og.title ? generateSlug(og.title) : prev.slug,
          summary: og.summary || prev.summary,
          cover_image: og.cover_image || prev.cover_image,
          media_source: og.media_source || prev.media_source,
          image_alt: og.title || prev.image_alt,
          external_url: og.external_url || targetUrl,
        }));
        setProxyImageSrc('');
        setImageLoadFailed(false);
        if (og.cover_image) setImageMode('url');
        setOgSuccess(true);
      }
    } catch (err) {
      console.warn('Auto-fetch OG failed:', err);
      setErrorMsg('Could not auto-fetch metadata from this link. You can fill the details manually below.');
    } finally {
      setFetchingOg(false);
    }
  };

  const handleUrlBlur = () => {
    // Auto-fetch if external_url is entered and title is currently blank
    if (formData.external_url.trim() && !formData.title.trim() && !fetchingOg) {
      handleFetchOg(formData.external_url);
    }
  };

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title: val,
      slug: autoSlug ? generateSlug(val) : prev.slug,
      image_alt: prev.image_alt ? prev.image_alt : val,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.external_url.trim() && !formData.title.trim()) {
      setErrorMsg('Please provide a PR website link or an article title.');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const payload = {
        ...formData,
        tags: formData.tags
          ? formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
      };

      if (editData?._id) {
        await axiosInstance.put(`/admin/news/${editData._id}`, payload);
      } else {
        await axiosInstance.post('/admin/news', payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Save News/PR error:', err);
      setErrorMsg(err.response?.data?.msg || 'Failed to save PR article.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={() => !saving && onClose()} maxWidth="md">
      <AdminDialogHeader
        title={editData ? 'Edit News / PR Card' : 'Add News / PR Link Card'}
        subtitle="Paste an external article or PR link to generate a preview card for the website"
        icon={Newspaper}
        onClose={() => !saving && onClose()}
      />
      <AdminDialogContent dividers>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}

        {ogSuccess && (
          <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }} icon={<CheckCircle2 size={18} />}>
            Successfully fetched OG metadata &amp; preview from the PR website!
          </Alert>
        )}

        {/* PRIMARY LINK INPUT SECTION */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2.5,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            border: '1.5px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.25),
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.dark', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Link2 size={18} /> PR Article Link (Website URL)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Paste the link from the news publication (e.g. Economic Times, YourStory, Livemint, etc.). We will automatically extract the title, OG image, and summary.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="https://yourstory.com/2024/... or https://economictimes.indiatimes.com/..."
              value={formData.external_url}
              onChange={(e) => handleChange('external_url', e.target.value)}
              onBlur={handleUrlBlur}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Globe size={16} color={theme.palette.text.secondary} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: 'background.paper', borderRadius: 1.5 }}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={fetchingOg || !formData.external_url.trim()}
              onClick={() => handleFetchOg()}
              startIcon={fetchingOg ? <CircularProgress size={16} color="inherit" /> : <Sparkles size={16} />}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                whiteSpace: 'nowrap',
                textTransform: 'none',
              }}
            >
              {fetchingOg ? 'Fetching...' : 'Fetch Details'}
            </Button>
          </Stack>
        </Paper>

        {/* LIVE CARD PREVIEW */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1, display: 'block' }}>
            Live Website Card Preview
          </Typography>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              maxWidth: 480,
              bgcolor: 'background.paper',
            }}
          >
            {/* Card Image */}
            <Box
              sx={{
                height: { xs: 210, sm: 240 },
                bgcolor: '#0f172a',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {formData.cover_image && !imageLoadFailed ? (
                <>
                  {/* Ambient blurred backdrop so letterboxing blends seamlessly with no harsh black margins */}
                  <Box
                    component="img"
                    src={proxyImageSrc || getNewsImageUrl(formData.cover_image)}
                    alt=""
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(20px) brightness(0.55)',
                      transform: 'scale(1.3)',
                    }}
                  />
                  {/* Foreground Image with selected framing */}
                  <Box
                    component="img"
                    src={proxyImageSrc || getNewsImageUrl(formData.cover_image)}
                    alt={formData.title || 'Card Cover Preview'}
                    referrerPolicy="no-referrer"
                    onError={() => {
                      const directUrl = getNewsImageUrl(formData.cover_image);
                      if (!proxyImageSrc && directUrl && directUrl.startsWith('http')) {
                        setProxyImageSrc(`${API_URL}/admin/news/proxy-image?url=${encodeURIComponent(directUrl)}`);
                      } else {
                        setImageLoadFailed(true);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      width: '100%',
                      height: '100%',
                      objectFit: formData.image_fit === 'cover' ? 'cover' : (formData.image_fit === 'top' ? 'cover' : 'contain'),
                      objectPosition: formData.image_fit === 'cover' ? 'center center' : 'top center',
                      display: 'block',
                    }}
                  />

                  {/* Image Framing Selector Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 3,
                      display: 'flex',
                      gap: 0.5,
                      bgcolor: 'rgba(15,23,42,0.8)',
                      p: 0.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <Tooltip title="Fit Whole Image (No Cropping - 100% visible)">
                      <Button
                        size="small"
                        onClick={() => handleChange('image_fit', 'contain')}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 1.5,
                          bgcolor: (!formData.image_fit || formData.image_fit === 'contain') ? 'primary.main' : 'transparent',
                          color: '#fff',
                          '&:hover': { bgcolor: (!formData.image_fit || formData.image_fit === 'contain') ? 'primary.dark' : 'rgba(255,255,255,0.1)' },
                        }}
                      >
                        Fit Whole
                      </Button>
                    </Tooltip>
                    <Tooltip title="Focus on Top / Heads (Preserves Faces)">
                      <Button
                        size="small"
                        onClick={() => handleChange('image_fit', 'top')}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 1.5,
                          bgcolor: formData.image_fit === 'top' ? 'primary.main' : 'transparent',
                          color: '#fff',
                          '&:hover': { bgcolor: formData.image_fit === 'top' ? 'primary.dark' : 'rgba(255,255,255,0.1)' },
                        }}
                      >
                        Top Focus
                      </Button>
                    </Tooltip>
                    <Tooltip title="Fill & Center (Full Width Cover)">
                      <Button
                        size="small"
                        onClick={() => handleChange('image_fit', 'cover')}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 1.5,
                          bgcolor: formData.image_fit === 'cover' ? 'primary.main' : 'transparent',
                          color: '#fff',
                          '&:hover': { bgcolor: formData.image_fit === 'cover' ? 'primary.dark' : 'rgba(255,255,255,0.1)' },
                        }}
                      >
                        Fill
                      </Button>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <Stack alignItems="center" spacing={1} sx={{ p: 2, textAlign: 'center', color: '#94a3b8' }}>
                  <ImageIcon size={32} opacity={0.6} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {imageLoadFailed ? 'Host blocked image. Use "Upload File" below.' : 'No cover image selected'}
                  </Typography>
                </Stack>
              )}

              <Box sx={{ position: 'absolute', bottom: 10, left: 10, zIndex: 2 }}>
                <Chip
                  label={formData.type.replace('_', ' ').toUpperCase()}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.95)', fontWeight: 800, fontSize: '0.68rem', height: 22, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                />
              </Box>
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formData.media_source || 'Publication / Source'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.published_date}
                </Typography>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  lineHeight: 1.35,
                  mb: 1,
                  color: 'text.primary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {formData.title || 'Headline / Article Title Preview'}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.825rem',
                  lineHeight: 1.5,
                  mb: 2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {formData.summary || 'Summary or description from the article page will appear here.'}
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                endIcon={<ExternalLink size={14} />}
                component="a"
                href={formData.external_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!formData.external_url}
                sx={{ borderRadius: 1.5, fontWeight: 700, textTransform: 'none' }}
              >
                Read on {formData.media_source || 'PR Website'}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* EDITABLE DETAILS */}
        <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1, display: 'block' }}>
          Card Information &amp; Controls
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Article / Headline Title"
              required
              fullWidth
              size="small"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Headline from the PR publication"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Media Source / Publisher"
              fullWidth
              size="small"
              value={formData.media_source}
              onChange={(e) => handleChange('media_source', e.target.value)}
              placeholder="e.g. Economic Times, YourStory, Livemint"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <MenuItem value="press_release">📰 Press Release</MenuItem>
                <MenuItem value="media_coverage">📺 Media Coverage</MenuItem>
                <MenuItem value="news">📢 Company News</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#f8fafc',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon size={18} color={theme.palette.primary.main} /> Article Cover Image
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={imageMode === 'upload' ? 'contained' : 'outlined'}
                    onClick={() => setImageMode('upload')}
                    startIcon={<Upload size={14} />}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, py: 0.4 }}
                  >
                    Upload File
                  </Button>
                  <Button
                    size="small"
                    variant={imageMode === 'url' ? 'contained' : 'outlined'}
                    onClick={() => setImageMode('url')}
                    startIcon={<Link2 size={14} />}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, py: 0.4 }}
                  >
                    Image URL
                  </Button>
                </Stack>
              </Stack>

              {imageMode === 'upload' ? (
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageFileUpload}
                  />

                  {uploadingImage ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <CircularProgress size={24} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                        Uploading image file to server...
                      </Typography>
                    </Box>
                  ) : formData.cover_image && !formData.cover_image.startsWith('http') ? (
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}
                    >
                      <Box
                        component="img"
                        src={getNewsImageUrl(formData.cover_image)}
                        alt="Uploaded"
                        sx={{ width: 54, height: 54, borderRadius: 1.5, objectFit: 'cover' }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                        >
                          {formData.cover_image}
                        </Typography>
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 700, display: 'block' }}>
                          ✓ Uploaded to Reseller Bazzar Media Server
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                      >
                        Change
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          handleChange('cover_image', '');
                          setProxyImageSrc('');
                          setImageLoadFailed(false);
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        p: 2.5,
                        border: '2px dashed #cbd5e1',
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: '#ffffff',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <Upload size={28} color={theme.palette.primary.main} style={{ margin: '0 auto 8px', display: 'block' }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Click to select an image from your device
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        JPG, PNG, or WebP (Will be saved directly to Reseller Bazzar storage)
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://... direct image URL or scraped OG image link"
                    value={formData.cover_image}
                    onChange={(e) => {
                      handleChange('cover_image', e.target.value);
                      setProxyImageSrc('');
                      setImageLoadFailed(false);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ImageIcon size={16} color={theme.palette.text.secondary} />
                        </InputAdornment>
                      ),
                      endAdornment: formData.cover_image && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleChange('cover_image', '');
                              setProxyImageSrc('');
                              setImageLoadFailed(false);
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: '#ffffff' }}
                  />

                  {formData.cover_image && formData.cover_image.startsWith('http') && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={savingExternalImage}
                        onClick={handleSaveExternalImage}
                        startIcon={savingExternalImage ? <CircularProgress size={14} color="inherit" /> : <Sparkles size={14} />}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, whiteSpace: 'nowrap' }}
                      >
                        {savingExternalImage ? 'Saving...' : 'Save to Our Server'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Permanently saves this external image to Reseller Bazzar so it never expires or gets blocked.
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}
            </Paper>
          </Grid>

          <Grid size={12}>
            <TextField
              label="Summary / Description"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={formData.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              placeholder="Brief summary or hook for the card"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Published Date"
              type="date"
              fullWidth
              size="small"
              value={formData.published_date}
              onChange={(e) => handleChange('published_date', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="published">Published (Visible on site)</MenuItem>
                <MenuItem value="draft">Draft (Hidden)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_featured}
                  onChange={(e) => handleChange('is_featured', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Feature on top of the Newsroom
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Displays this card prominently in the top spotlight banner on the website
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </AdminDialogContent>

      <AdminDialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit" sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving || (!formData.external_url.trim() && !formData.title.trim())}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Newspaper size={16} />}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Saving...' : editData ? 'Update Card' : 'Save & Publish Card'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

export default NewsPrModal;
